// Pure sort / group / filter helpers for the Everyday › Tasks tab, shared by web and native.
import { logicalDayStr } from './day'

export type TaskSortKey = 'default' | 'due' | 'priority' | 'created' | 'title'
export type TaskGroupKey = 'none' | 'due' | 'created' | 'priority' | 'tag'
export type TaskDueKey = 'overdue' | 'today' | 'week' | 'none'
export type TaskPriority = 'super' | 'urgent' | 'normal'
export type TaskRepeatKey = 'repeating' | 'oneTime'

export interface TaskFilter {
  sort: TaskSortKey
  group: TaskGroupKey
  due: TaskDueKey[]
  priorities: TaskPriority[]
  doToday: boolean
  statuses: string[]
  tags: string[]
  repeat: TaskRepeatKey[]
}

export const defaultTaskFilter: TaskFilter = {
  sort: 'default',
  group: 'created',
  due: [],
  priorities: [],
  doToday: false,
  statuses: [],
  tags: [],
  repeat: [],
}

export const TASK_DEFAULT_GROUP: TaskGroupKey = 'created'

export type DueBucket = 'overdue' | 'today' | 'tomorrow' | 'week' | 'later' | 'none'
export const DUE_BUCKETS: readonly DueBucket[] = [
  'overdue',
  'today',
  'tomorrow',
  'week',
  'later',
  'none',
]
export const PRIORITIES: readonly TaskPriority[] = ['super', 'urgent', 'normal']

export type DayCtx = { now: number; tzOffsetMin: number; dayStartHour: number }

const DAY_MS = 86_400_000

/** Day bucket of a due date on the user's logical day: week = 2-6 days ahead, later = 7+. */
export function dueBucket(dueDate: number | null | undefined, ctx: DayCtx): DueBucket {
  if (dueDate == null) return 'none'
  const key = (ms: number) =>
    Date.parse(`${logicalDayStr(ms, ctx.tzOffsetMin, ctx.dayStartHour)}T00:00:00Z`)
  const days = Math.round((key(dueDate) - key(ctx.now)) / DAY_MS)
  if (days < 0) return 'overdue'
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  return days < 7 ? 'week' : 'later'
}

/** Super urgent only counts on an urgent (`high`) task, as everywhere else. */
export function priorityOf(t: { effort: string; superUrgent?: boolean | null }): TaskPriority {
  if (t.effort !== 'high') return 'normal'
  return t.superUrgent ? 'super' : 'urgent'
}

const priorityRank = (p: TaskPriority) => PRIORITIES.indexOf(p)

export type TaskView = {
  title: string
  tags: string[]
  dueDate: number | null | undefined
  effort: string
  superUrgent?: boolean | null
  /** 'YYYY-MM-DD' the task is committed to; carries over until done. */
  mustDoOn: string | null | undefined
  recurs: boolean
  /** 'pending' | 'in-progress' */
  status: string
  created: number
}

/** The "This week" filter covers tomorrow through six days ahead; "Today" is separate. */
const matchesDue = (due: readonly TaskDueKey[], b: DueBucket) =>
  due.length === 0 || due.some((d) => (d === 'week' ? b === 'tomorrow' || b === 'week' : d === b))

export function filterTasks<T>(
  items: T[],
  f: Omit<TaskFilter, 'sort' | 'group'>,
  view: (x: T) => TaskView,
  ctx: DayCtx,
): T[] {
  const today = logicalDayStr(ctx.now, ctx.tzOffsetMin, ctx.dayStartHour)
  return items.filter((x) => {
    const v = view(x)
    if (f.statuses.length && !f.statuses.includes(v.status)) return false
    if (f.due.length && !matchesDue(f.due, dueBucket(v.dueDate, ctx))) return false
    if (f.priorities.length && !f.priorities.includes(priorityOf(v))) return false
    if (f.doToday && !(v.mustDoOn != null && v.mustDoOn <= today)) return false
    if (f.tags.length && !v.tags.some((t) => f.tags.includes(t))) return false
    if (f.repeat.length && !f.repeat.includes(v.recurs ? 'repeating' : 'oneTime')) return false
    return true
  })
}

/** Sorts a copy. Default keeps the caller's order; ties keep it too. */
export function sortTasks<T>(items: T[], sort: TaskSortKey, view: (x: T) => TaskView): T[] {
  const s = [...items]
  switch (sort) {
    case 'due':
      return s.sort((a, b) => (view(a).dueDate ?? Infinity) - (view(b).dueDate ?? Infinity))
    case 'priority':
      return s.sort((a, b) => priorityRank(priorityOf(view(a))) - priorityRank(priorityOf(view(b))))
    case 'created':
      return s.sort((a, b) => view(b).created - view(a).created)
    case 'title':
      return s.sort((a, b) =>
        view(a).title.localeCompare(view(b).title, undefined, { sensitivity: 'base' }),
      )
    default:
      return s
  }
}

export type TaskSection<T> = { key: string; items: T[] }

/** Due-date, priority, tag and flat groups, in a stable header order with empty sections dropped. "created" is rendered by the caller. */
export function groupTasks<T>(
  items: T[],
  group: Exclude<TaskGroupKey, 'created'>,
  view: (x: T) => TaskView,
  ctx: DayCtx,
  allTags: readonly string[],
): TaskSection<T>[] {
  const by = (keys: readonly string[], pick: (v: TaskView) => string) =>
    keys
      .map((key) => ({ key, items: items.filter((x) => pick(view(x)) === key) }))
      .filter((s) => s.items.length > 0)
  switch (group) {
    case 'none':
      return [{ key: 'all', items }]
    case 'due':
      return by(DUE_BUCKETS, (v) => dueBucket(v.dueDate, ctx))
    case 'priority':
      return by(PRIORITIES, priorityOf)
    case 'tag': {
      const out = allTags
        .map((key) => ({ key, items: items.filter((x) => view(x).tags.includes(key)) }))
        .filter((s) => s.items.length > 0)
      const none = items.filter((x) => view(x).tags.length === 0)
      return none.length ? [...out, { key: '', items: none }] : out
    }
  }
}

type LegacyInboxFilter = {
  efforts?: string[]
  sort?: string
  group?: string
  tags?: string[]
  statuses?: string[]
}

/** Carries a saved pre-TaskFilter `inboxFilter` over; anything unrecognised falls back to the default. */
export function taskFilterFromLegacy(old: LegacyInboxFilter | null | undefined): TaskFilter {
  if (!old || typeof old !== 'object') return defaultTaskFilter
  const list = (x: unknown) => (Array.isArray(x) ? x.filter((s) => typeof s === 'string') : [])
  const sort: Record<string, TaskSortKey> = { effort: 'priority', category: 'due' }
  const group: Record<string, TaskGroupKey> = {
    none: 'none',
    created: 'created',
    category: 'priority',
    tag: 'tag',
  }
  const efforts = list(old.efforts)
  return {
    ...defaultTaskFilter,
    sort: sort[old.sort ?? ''] ?? 'default',
    group: group[old.group ?? ''] ?? TASK_DEFAULT_GROUP,
    priorities: efforts.includes('high') ? ['super', 'urgent'] : efforts.length ? ['normal'] : [],
    statuses: list(old.statuses),
    tags: list(old.tags),
  }
}
